import { Text } from '@chakra-ui/react';
import { TextLink } from '@time-line/components/common';
import {
  RichTextResolver,
  SimpleTextContainer,
} from 'components/common/RichTextResolver';
import { useProductEnabledCountries } from 'hooks/localisation/useProductEnabledCountries';
import { useGlobals } from 'hooks/useGlobals';
import { useLocalStorage, UserLocale } from 'hooks/userPreferences';
import { useSiteSpecificRoutes } from 'hooks/useSiteSpecificRoutes';
import { env } from 'lib/constants/env';
import NextLink from 'next/link';
import { isFreeShippingCountry } from 'utils/localisation/isFreeShippingCountry';

import { Item } from './Item';

interface ShippingProps {
  productId?: string;
}

const SHIPPING_TEXT = {
  free: 'Free shipping',
  calculated: 'Shipping calculated at checkout',
};

const isCountrySelectorEnabled = env.NEXT_PUBLIC_COUNTRY_SELECTOR_ENABLED;

const DefaultShippingInfo = () => {
  const {
    data: {
      siteSettings: { shippingInfo },
    },
  } = useGlobals();

  return (
    <Item isChecked>
      <RichTextResolver blocks={shippingInfo} container={SimpleTextContainer} />
    </Item>
  );
};

export function Shipping({ productId }: ShippingProps) {
  const {
    value: { countryCode, countryName },
  } = useLocalStorage(UserLocale);

  const [SHIPPING_POLICY] = useSiteSpecificRoutes(['SHIPPING_POLICY']);

  const { data: productEnabledCountries } =
    useProductEnabledCountries(productId);

  if (!isCountrySelectorEnabled) {
    return <DefaultShippingInfo />;
  }

  const isShippingEnabled =
    isCountrySelectorEnabled &&
    !!productEnabledCountries &&
    productEnabledCountries.includes(countryCode);

  if (isShippingEnabled) {
    const isFreeShipping = isFreeShippingCountry(countryCode);
    return (
      <Item isChecked>
        {isFreeShipping ? SHIPPING_TEXT.free : SHIPPING_TEXT.calculated}
      </Item>
    );
  }

  return (
    <Item isChecked={false}>
      <Text size="detail">
        {`This product doesn’t ship to ${countryName} · `}
        <TextLink
          as={NextLink}
          href={SHIPPING_POLICY}
          variant="underline"
          textProps={{
            size: 'inherit',
          }}
        >
          Shipping policy
        </TextLink>
      </Text>
    </Item>
  );
}
